import React from "react";
import { SectionContentProps } from "../../types";
import EditableText from "../../core/EditableText";
import { PiStarFill, PiPlus, PiTrash, PiUserCircle, PiQuotesFill } from "react-icons/pi";

const THEME_COLORS = [
    { name: "red", bg: "bg-rose-500", text: "text-rose-500", border: "border-rose-500" },
    { name: "orange", bg: "bg-orange-400", text: "text-orange-400", border: "border-orange-400" },
    { name: "teal", bg: "bg-teal-500", text: "text-teal-500", border: "border-teal-500" },
    { name: "blue", bg: "bg-blue-500", text: "text-blue-500", border: "border-blue-500" },
    { name: "purple", bg: "bg-purple-500", text: "text-purple-500", border: "border-purple-500" },
];

export default function TestimonialsModern({ content, onContentChange, isSelected }: SectionContentProps) {
    const fontFamily = content.fontFamily || "'Inter', sans-serif";

    // Default data if none exists
    const testimonials = content.testimonials || [
        { name: "CLIENT NAME", role: "Title Here", text: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna.", color: "red" },
        { name: "CLIENT NAME", role: "Title Here", text: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna.", color: "orange" },
        { name: "CLIENT NAME", role: "Title Here", text: "Lorem ipsum dolor sit amet, consectetuer adipiscing elit, sed diam nonummy nibh euismod tincidunt ut laoreet dolore magna.", color: "teal" },
    ];

    const addTestimonial = () => {
        const nextColorIdx = testimonials.length % THEME_COLORS.length;
        const newItems = [...testimonials, {
            name: "CLIENT NAME",
            role: "Title Here",
            text: "Add your review text here.",
            color: THEME_COLORS[nextColorIdx].name
        }];
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const removeTestimonial = (idx: number) => {
        const newItems = testimonials.filter((_: any, i: number) => i !== idx);
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const updateTestimonial = (idx: number, field: string, value: string) => {
        const newItems = [...testimonials];
        newItems[idx] = { ...newItems[idx], [field]: value };
        onContentChange?.({ ...content, testimonials: newItems });
    };

    const getColorTheme = (colorName: string, index: number) => {
        // Fallback to cycling colors if specific color not defined
        if (!colorName) return THEME_COLORS[index % THEME_COLORS.length];
        return THEME_COLORS.find(c => c.name === colorName) || THEME_COLORS[index % THEME_COLORS.length];
    };

    return (
        <div
            className={`bg-gray-100 px-4 py-20 ${isSelected ? "ring-2 ring-blue-500 ring-offset-2" : ""}`}
            style={{ fontFamily }}
        >
            <div className="mx-auto max-w-7xl">
                {/* Optional Section Title - standard for sections even if cards display "Testimonials" */}
                {content.title && (
                    <h2 className="mb-12 text-center text-4xl font-bold text-gray-900 tracking-tight">
                        <EditableText
                            value={content.title}
                            onChange={(val) => onContentChange?.({ ...content, title: val })}
                            as="span"
                            placeholder="Section Title"
                        />
                    </h2>
                )}

                <div className="flex flex-wrap justify-center gap-8">
                    {testimonials.map((item: any, idx: number) => {
                        const theme = getColorTheme(item.color, idx);

                        return (
                            <div
                                key={idx}
                                className="group relative w-full sm:w-[350px] shrink-0 overflow-hidden"
                            >
                                {/* Delete Button */}
                                {isSelected && (
                                    <button
                                        onClick={(e) => { e.stopPropagation(); removeTestimonial(idx); }}
                                        className="absolute top-2 right-2 z-20 hidden group-hover:flex h-8 w-8 items-center justify-center rounded-full bg-white/10 backdrop-blur-sm text-white hover:bg-red-500 shadow-lg transition-all"
                                        title="Remove"
                                    >
                                        <PiTrash className="h-4 w-4" />
                                    </button>
                                )}

                                <div className="relative flex flex-col bg-[#0f172a] shadow-2xl h-full pb-6">
                                    {/* Top Header Section */}
                                    <div className="pt-8 pb-16 text-center relative z-10">
                                        <div className={`font-cursive text-3xl ${theme.text} mb-1 opacity-90`} style={{ fontFamily: '"Brush Script MT", "cursive", sans-serif' }}>
                                            client
                                        </div>
                                        <h3 className="text-xl font-bold text-white uppercase tracking-[0.2em]">
                                            TESTIMONIALS
                                        </h3>
                                    </div>

                                    {/* Curved Colored Background */}
                                    {/* Using a pseudo-element or container with specific radius to create the hill effect */}
                                    <div className="relative isolate">
                                        {/* The Hill Shape */}
                                        <div className={`absolute -top-12 left-0 right-0 h-24 ${theme.bg} rounded-[50%_50%_0_0/100%_100%_0_0] z-0`}></div>
                                        <div className={`relative ${theme.bg} pt-12 pb-8 px-6 min-h-[320px] z-0`}>

                                            {/* Avatar - Positioned overlapping the curve */}
                                            <div className="absolute -top-[70px] left-1/2 -translate-x-1/2 z-10">
                                                <div className="h-24 w-24 rounded-full bg-gray-300 border-4 border-[#0f172a] overflow-hidden flex items-center justify-center text-gray-500">
                                                    {item.image || item.avatar ? (
                                                        <img src={item.image || item.avatar} alt={item.name} className="h-full w-full object-cover" />
                                                    ) : (
                                                        <PiUserCircle className="h-16 w-16 text-gray-400" />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Client Name & Title */}
                                            <div className="text-center text-white mb-6 mt-2">
                                                <div className="font-bold uppercase tracking-wider text-sm">
                                                    <EditableText
                                                        value={item.name}
                                                        onChange={(val) => updateTestimonial(idx, "name", val)}
                                                        as="span"
                                                        placeholder="CLIENT NAME"
                                                    />
                                                </div>
                                                <div className="text-xs opacity-80 mt-1">
                                                    <EditableText
                                                        value={item.role}
                                                        onChange={(val) => updateTestimonial(idx, "role", val)}
                                                        as="span"
                                                        placeholder="Title Here"
                                                    />
                                                </div>
                                            </div>

                                            {/* Quote Bubble */}
                                            <div className="relative bg-[#d1e8ef] rounded-3xl p-6 text-center shadow-sm">
                                                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                                                    <PiQuotesFill className="text-gray-600 h-6 w-6" />
                                                </div>

                                                <p className="text-xs text-gray-700 leading-relaxed mb-4 pt-2">
                                                    <EditableText
                                                        value={item.text}
                                                        onChange={(val) => updateTestimonial(idx, "text", val)}
                                                        as="span"
                                                        placeholder="Testimonial text goes here..."
                                                    />
                                                </p>

                                                <div className="flex justify-center gap-1 text-orange-400 pb-2">
                                                    {[1, 2, 3, 4, 5].map((s) => (
                                                        <PiStarFill key={s} className="h-3 w-3" />
                                                    ))}
                                                </div>

                                                {/* Speech bubble pointer (down) */}
                                                <div className="absolute -bottom-2 left-1/2 -translate-x-1/2 w-8 h-8 bg-[#d1e8ef] rotate-45 z-[-1] rounded-sm"></div>
                                            </div>

                                            {/* Know More Button */}
                                            <div className="mt-8 text-center">
                                                <button className="rounded-2xl bg-[#0f172a] px-6 py-1.5 text-[10px] font-bold text-white tracking-widest hover:bg-gray-900 transition-colors uppercase">
                                                    Know More
                                                </button>
                                            </div>

                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}

                    {isSelected && (
                        <button
                            onClick={addTestimonial}
                            className="flex min-h-[400px] w-full sm:w-[350px] flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-white/50 p-8 text-gray-400 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-500 transition-all"
                        >
                            <PiPlus className="h-10 w-10 mb-2" />
                            <span className="text-lg font-medium">Add New Card</span>
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
